import httpPlatform from './httpPlatform'

var pollInstance = httpPlatform.create({ timeout: httpPlatform.POLL_TIMEOUT_MS })

// Per-division scan mode that disables receipt capture (PRODUCTDEV-5475/5788). Set from the
// division-settings "No Receipt Capture" admin radio (Legacy) and persisted to
// platform.ReceiptScanSetting as the canonical value 'scanning_disabled'. The mobile FAB
// (mobile.vue) and the Reconcile capture UI react by hiding scan affordances and uploading
// receipts without sending them to Photon. Any division whose prefillMode equals this is "no capture".
export const NO_RECEIPT_CAPTURE_MODE = 'scanning_disabled'

const receiptScansApi = {
  // PRODUCTDEV-5805 — targetDivisionId is the DivisionId of the transaction being reconciled.
  // A folder-picked receipt isn't matched yet, so the BE can't resolve the target division's
  // scan-detail mode without it and would default to subtotal_and_tax. Optional + backward
  // compatible: omit it (e.g. inline-prefill-during-scan) and the BE keeps its existing behavior.
  getPrefill (receiptScanId, targetDivisionId) {
    return pollInstance.post(`/api/v1/receipts/${receiptScanId}/prefill`,
      targetDivisionId != null ? { targetDivisionId: targetDivisionId } : {})
  },

  getStatus (receiptScanId, config) {
    return pollInstance.get(`/api/v1/receipts/${receiptScanId}`, config)
  },

  getImage (receiptScanId) {
    return httpPlatform.get(`/api/v1/receipts/${receiptScanId}/image`)
  },

  // Streams the raw image bytes through Platform (bypasses S3 CORS). Used by the
  // folder-pick flow to get a Blob for both the local preview and the Legacy
  // re-upload. Returns a Blob directly; size + type are read off the Blob.
  // Wrap in try/catch — 410 Gone means the image was disposed; other errors
  // (401/403/404) fall through the standard axios error handler.
  getImageBytes (receiptScanId) {
    return httpPlatform.get(`/api/v1/receipts/${receiptScanId}/image-bytes`, {
      responseType: 'blob'
    })
  },

  // Per-division receipt scan mode. Cardholder-readable (server-side ownership check:
  // the division must belong to the caller's client); a missing row resolves to the
  // default mode (capture enabled). Resolves to { legacyAccountId, prefillMode, ... }.
  // Used by mobile to decide whether receipt capture is disabled for a division.
  getReceiptScanSetting (divisionId) {
    return httpPlatform.get(`/api/v1/receipt-scan-settings/${divisionId}`)
  },

  submitReceipt (formData, idempotencyKey, extra) {
    if (extra) {
      if (extra.uploadContextLegacyTransactionId != null) {
        formData.append('uploadContextLegacyTransactionId', extra.uploadContextLegacyTransactionId)
      }
      if (extra.uploadContextLegacyCardId != null) {
        formData.append('uploadContextLegacyCardId', extra.uploadContextLegacyCardId)
      }
      // DivisionId of the approval channel the user uploaded from (customer-facing
      // naming varies; in code it's a Division). BE narrows the ranker pool to
      // Programs in this Division. Omit when no channel context (mobile, folder).
      if (extra.uploadContextLegacyDivisionId != null) {
        formData.append('uploadContextLegacyDivisionId', extra.uploadContextLegacyDivisionId)
      }
      if (extra.uploadSurface != null) {
        formData.append('uploadSurface', extra.uploadSurface)
      }
    }
    return httpPlatform.post('/api/v1/receipts', formData, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}
    })
  },

  cancelScan (receiptScanId) {
    return httpPlatform.post(`/api/v1/receipts/${receiptScanId}/cancel`)
  },

  matchToTransaction (receiptScanId, legacyTransactionId, rowVersion) {
    return httpPlatform.post(`/api/v1/receipts/${receiptScanId}/match`, {
      selectedLegacyTransactionId: legacyTransactionId,
      rowVersion: rowVersion || ''
    })
  },

  searchReceipts (filters = {}, page = 1, pageSize = 50) {
    var body = { page: page, pageSize: pageSize }
    if (filters.status != null) body.status = filters.status
    if (filters.fromDate != null) body.fromDate = filters.fromDate
    if (filters.toDate != null) body.toDate = filters.toDate
    if (filters.uploadContextLegacyTransactionId != null) {
      body.uploadContextLegacyTransactionId = filters.uploadContextLegacyTransactionId
    }
    // orderBy: 'UploadedAt' | 'TransactionDate' | 'MatchScore' (mapped to SearchReceiptsQuery.OrderBy server-side).
    // sortDirection: 'asc' | 'desc' (default 'desc').
    if (filters.orderBy != null) body.orderBy = filters.orderBy
    if (filters.sortDirection != null) body.sortDirection = filters.sortDirection
    // Match-ranking inputs (mobile folder picker). When matchAgainstLegacyTransactionId is set the
    // backend auto-ranks the result set by match score against that transaction; the explicit
    // matchTxn* fields let a caller pass amount/date/merchant directly when there's no txn id.
    if (filters.matchTxnAmount != null) body.matchTxnAmount = filters.matchTxnAmount
    if (filters.matchTxnDate != null) body.matchTxnDate = filters.matchTxnDate
    if (filters.matchTxnMerchant != null) body.matchTxnMerchant = filters.matchTxnMerchant
    if (filters.matchAgainstLegacyTransactionId != null) {
      body.matchAgainstLegacyTransactionId = filters.matchAgainstLegacyTransactionId
    }
    return httpPlatform.post('/api/v1/receipts/search', body)
  },

  saveToFolder (receiptScanId, rowVersion) {
    return httpPlatform.post(`/api/v1/receipts/${receiptScanId}/save-to-folder`, {
      rowVersion: rowVersion || ''
    })
  },

  claimReceipt (receiptScanId, selectedTxId, rowVersion) {
    return httpPlatform.post(`/api/v1/receipts/${receiptScanId}/claim`, {
      selectedLegacyTransactionId: selectedTxId,
      rowVersion: rowVersion || ''
    })
  },

  deleteReceipt (receiptScanId) {
    return httpPlatform.delete(`/api/v1/receipts/${receiptScanId}`)
  },

  // PRODUCTDEV-5788 — per-division receipt-scan mode. Public, tenant-authorized GET
  // on the int-constrained route. Returns the GetReceiptScanSettingDto incl.
  // `prefillMode` (one of: subtotal_and_tax / total_only / line_items_and_tax /
  // scanning_disabled). A no-row division returns the Platform default.
  getScanMode (legacyAccountId) {
    return httpPlatform.get(`/api/v1/receipt-scan-settings/${legacyAccountId}`)
  },

  // PRODUCTDEV-5788 — tenant-wide "is any division scan-enabled?" check used by the
  // global all-transactions Submit Receipt button (no single division in context).
  // Returns { anyEnabled: true | false }; true iff >=1 in-scope division is not
  // scanning_disabled.
  getAnyScanEnabled () {
    return httpPlatform.get('/api/v1/receipt-scan-settings/any-enabled')
  }
}

export default receiptScansApi



// WEBPACK FOOTER //
// ./src/core/api/receiptScans.js